import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Select from "react-select";
import { useOutlet } from '../../../context/OutletContext';
import PageStatus from '../../PageStatus/PageStatus';

const isVoidItem = (item) =>
    item.completedAt?.toLowerCase() === "void" ||
    item.TotalTime?.toLowerCase() === "void";

const KitchenItemReport = () => {
    const today = new Date().toLocaleDateString('en-CA');
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [itemlist, setItemlist] = useState([]);
    const [selectedItem, setSelectedItem] = useState("");
    const [iData, setIData] = useState([]);
    const [isDisabled, setIsDisabled] = useState(true);
    const [searched, setSearched] = useState(false);
    const [showVoid, setShowVoid] = useState(true);
    const [showNonVoid, setShowNonVoid] = useState(true);

    const { defaultOutlet } = useOutlet();
    const selectedOutlet = defaultOutlet;

    const fetchItems = useCallback(async () => {
        if (!selectedOutlet || !token) return;
        try {
            setLoading(true);
            setError(null);
            const itemResponse = await axios.post(
                `${baseUrl}/ordertrackeritem-lists`,
                { outlet: selectedOutlet, token }
            );
            const formattedItems = itemResponse.data.items.map((item) => ({
                label: item,
                value: item,
            }));
            setItemlist(formattedItems);
            setIsDisabled(false);
        } catch (err) {
            setError(
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                "Failed to fetch items list"
            );
        } finally {
            setLoading(false);
        }
    }, [selectedOutlet, token, baseUrl]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!selectedOutlet || !token) return;
        if (!selectedItem.trim()) {
            setError("Please select an item");
            return;
        }
        setLoading(true);
        setError(null);
        setSearched(true);
        try {
            const response = await axios.post(
                `${baseUrl}/items-orders-datewise`,
                { startDate, endDate, outletName: selectedOutlet, itemName: selectedItem }
            );
            // Store raw data — filtering happens at render via checkboxes
            setIData(response.data.orders || []);
        } catch (err) {
            setError(
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                "Failed to fetch item data"
            );
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateStr).toLocaleDateString(undefined, options);
    };

    // Filtering happens here, reactively, based on checkbox state
    const filteredData = iData.map(dateGroup => ({
        ...dateGroup,
        orders: dateGroup.orders
            .map(order => ({
                ...order,
                items: order.items.filter(item => {
                    const voidItem = isVoidItem(item);
                    return (showVoid && voidItem) || (showNonVoid && !voidItem);
                })
            }))
            .filter(order => order.items.length > 0)
    })).filter(dateGroup => dateGroup.orders.length > 0);

    // ── Inline styles ──────────────────────────────────────────────────────────
    const S = {
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
            textAlign: 'center',
        },
        thead: {
            position: 'sticky',
            top: 0,
            zIndex: 20,
            background: '#fff',
        },
        th: {
            padding: '10px 8px',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: '#8A8780',
            whiteSpace: 'nowrap',
            borderBottom: '1px solid #F0EDE8',
        },
        dateRow: {
            background: '#F8F7F3',
            borderTop: '1px solid #F0EDE8',
            borderBottom: '1px solid #F0EDE8',
        },
        dateTd: {
            padding: '8px 16px',
            textAlign: 'left',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#807e76',
        },
        rowNormal: {
            borderBottom: '1px solid #F8F7F3',
            background: '#fff',
            transition: 'background 0.1s',
        },
        rowVoid: {
            borderBottom: '1px solid #fecaca',
            background: '#FEF2F2',
            transition: 'background 0.1s',
        },
        tdNormal: {
            padding: '8px 10px',
            whiteSpace: 'nowrap',
            fontWeight: 500,
            color: '#5A5854',
        },
        tdVoid: {
            padding: '8px 10px',
            whiteSpace: 'nowrap',
            fontWeight: 500,
            color: '#DC2626',
        },
        voidBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '3px 10px',
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: 600,
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            color: '#DC2626',
        },
        voidDot: {
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: '#DC2626',
            flexShrink: 0,
        },
        muted: {
            color: '#C7C3BD',
        },
    };

    return (
        <div className='px-6 py-4'>
            <div className='max-w-6xl mx-auto flex flex-col gap-3'>

                {/* FILTER CARD */}
                <div className="rounded-lg border border-[#E0DDD8] bg-white overflow-hidden">
                    <div className="flex flex-wrap items-center justify-between text-xs px-4 py-3 gap-3">
                        <div className="flex gap-2 items-center">
                            <label className="font-semibold uppercase tracking-wider text-[#807e76]">
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="border border-[#E0DDD8] px-2 py-1.5 rounded-md bg-[#FAFAF8] text-[#2C2C2A] font-medium w-[120px] cursor-pointer"
                            />
                        </div>

                        <div className="flex gap-2 items-center">
                            <label className="font-semibold uppercase tracking-wider text-[#807e76]">
                                End Date
                            </label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="border border-[#E0DDD8] px-2 py-1.5 rounded-md bg-[#FAFAF8] text-[#2C2C2A] font-medium w-[120px] cursor-pointer"
                            />
                        </div>

                        <Select
                            options={itemlist}
                            value={itemlist.find(item => item.value === selectedItem) || null}
                            onChange={(option) => setSelectedItem(option?.value || "")}
                            isDisabled={isDisabled}
                            isSearchable
                            isClearable
                            placeholder="Search and select an item..."
                            className="text-sm flex-1 min-w-[240px] max-w-sm"
                            classNamePrefix="item-select"
                            styles={{
                                menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 50,
                                }),
                                control: (base, state) => ({
                                    ...base,
                                    minHeight: "34px",
                                    borderRadius: "6px",
                                    borderColor: state.isFocused ? "#BDBBB2" : "#E0DDD8",
                                    boxShadow: "none",
                                    backgroundColor: "#FAFAF8",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    "&:hover": { borderColor: "#BDBBB2" },
                                }),
                                valueContainer: (base) => ({ ...base, padding: "0 10px" }),
                                input: (base) => ({ ...base, margin: 0, padding: 0, color: "#2C2C2A", fontSize: "13px" }),
                                placeholder: (base) => ({ ...base, color: "#B4B2A9", fontSize: "13px" }),
                                singleValue: (base) => ({ ...base, color: "#2C2C2A", fontSize: "13px", fontWeight: 500 }),
                                menu: (base) => ({
                                    ...base,
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    marginTop: "4px",
                                    border: "1px solid #E0DDD8",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                                    zIndex: 50,
                                }),
                                menuList: (base) => ({ ...base, padding: "4px" }),
                                option: (base, state) => ({
                                    ...base,
                                    fontSize: "13px",
                                    borderRadius: "5px",
                                    padding: "8px 10px",
                                    backgroundColor: state.isSelected ? "#ECEAE3" : state.isFocused ? "#F5F4F0" : "transparent",
                                    color: "#2C2C2A",
                                    cursor: "pointer",
                                    fontWeight: state.isSelected ? 500 : 400,
                                }),
                                clearIndicator: (base) => ({ ...base, padding: "4px", color: "#C7C3BD", cursor: "pointer", "&:hover": { color: "#5A5854" } }),
                                dropdownIndicator: (base) => ({ ...base, padding: "4px", color: "#C7C3BD", "&:hover": { color: "#5A5854" } }),
                                indicatorSeparator: () => ({ display: "none" }),
                                noOptionsMessage: (base) => ({ ...base, fontSize: "12px", color: "#AAA69D", padding: "8px 10px" }),
                            }}
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                        />

                        <button
                            onClick={handleSearch}
                            disabled={loading}
                            className="px-3 py-2 rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 disabled:opacity-50 transition"
                        >
                            {loading ? 'Loading...' : 'Search'}
                        </button>
                    </div>
                </div>

                {/* STATUS */}
                <PageStatus loading={loading} error={error} loadingText="Loading data..." />

                {/* RESULTS */}
                {!loading && !error && searched && iData.length > 0 && (
                    <div className="rounded-lg border border-[#E0DDD8] bg-white overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 150px)' }}>

                        {/* Checkboxes — fixed, never scrolls away */}
                        <div className="flex-shrink-0 px-4 py-2.5 border-b border-[#F0EDE8] bg-[#F8F7F3] flex flex-wrap gap-4 items-center">
                            <span className="text-xs font-semibold tracking-[0.15em] uppercase text-[#807e76]">
                                Filter
                            </span>
                            <label className="flex items-center gap-1.5 text-xs font-medium text-[#5A5854] cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={showVoid}
                                    onChange={() => setShowVoid(v => !v)}
                                    className="accent-purple-600"
                                />
                                Show Void
                            </label>
                            <label className="flex items-center gap-1.5 text-xs font-medium text-[#5A5854] cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={showNonVoid}
                                    onChange={() => setShowNonVoid(v => !v)}
                                    className="accent-purple-600"
                                />
                                Show Non-Void
                            </label>
                        </div>

                        {filteredData.length === 0 ? (
                            <div style={{ padding: '40px 16px', textAlign: 'center', fontSize: '13px', color: '#AAA69D' }}>
                                No records match the current filter.
                            </div>
                        ) : (
                            // This div is the scroll container — thead stays sticky within it
                            <div className="overflow-auto tiny-scrollbar flex-1">
                                <table style={S.table}>
                                    <thead style={S.thead}>
                                        <tr>
                                            {["Date", "Ordered At", "Completed At", "Total Time", "Avg Prep Time", "Quantity", "KOT ID", "Table No."].map(h => (
                                                <th key={h} style={S.th}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((dateGroup, di) => (
                                            <React.Fragment key={di}>
                                                <tr style={S.dateRow}>
                                                    <td colSpan={8} style={S.dateTd}>
                                                        {formatDate(dateGroup.Date)}
                                                    </td>
                                                </tr>
                                                {dateGroup.orders.map((order, oi) =>
                                                    order.items.map((item, ii) => {
                                                        const isVoid = isVoidItem(item);
                                                        const rowStyle = isVoid ? S.rowVoid : S.rowNormal;
                                                        const tdStyle = isVoid ? S.tdVoid : S.tdNormal;
                                                        return (
                                                            <tr key={`${oi}-${ii}`} style={rowStyle}>
                                                                <td style={S.tdNormal} />
                                                                <td style={tdStyle}>{item.orderedAt}</td>
                                                                <td style={S.tdNormal}>
                                                                    {isVoid ? (
                                                                        <span style={S.voidBadge}>
                                                                            <span style={S.voidDot} />
                                                                            Void
                                                                        </span>
                                                                    ) : (item.completedAt || <span style={S.muted}>—</span>)}
                                                                </td>
                                                                <td style={tdStyle}>{item.TotalTime || <span style={S.muted}>—</span>}</td>
                                                                <td style={S.tdNormal}>
                                                                    {!item.AvgPrepTime || item.AvgPrepTime === "00:00:00"
                                                                        ? <span style={S.muted}>—</span>
                                                                        : item.AvgPrepTime}
                                                                </td>
                                                                <td style={tdStyle}>{item.Quantity}</td>
                                                                <td style={tdStyle}>{order.KOTID}</td>
                                                                <td style={tdStyle}>{order.TableNum}</td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Empty state — no data from API at all */}
                {!loading && !error && searched && iData.length === 0 && (
                    <div className="rounded-lg border border-[#E0DDD8] bg-white px-4 py-10 text-center text-sm text-[#AAA69D]">
                        No records found for the selected filters.
                    </div>
                )}
            </div>
        </div>
    );
};

export default KitchenItemReport;